import Input from "../../../components/custom/forms/Input";
import CheckBox from "../../../components/custom/forms/CheckBox";
import { useContext, useEffect, useMemo, useState, useImperativeHandle, forwardRef } from "react";
import { contextProvider } from "../../../../context/ContextProvider";
import Alerts from "../../../components/Alerts/Alerts";
import { Validate } from "../../../../utils/EventoUtils";
import ReactQuill from "react-quill";
import "./quill.snow.css";
import RestPrivateAntesala from "../../../../helpers/RestPrivateApi";

const Que = forwardRef(({ setForm, form, setValidation, validation, categorias, materialPendiente, setMaterialPendiente }, ref) => {
  const [materialDifusion, setMaterialDifusion] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingPrincipal, setIsDraggingPrincipal] = useState(false);
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [imagePreviewModal, setImagePreviewModal] = useState(null);
  const context = useContext(contextProvider);
  const { FORMs, VALIDATIONs, URLs } = context;
  const { VALIDATION_FORM_EVENTO } = VALIDATIONs;

  // Cargar material de difusión al montar el componente
  useEffect(() => {
    if (form.id && form.id[0]) {
      cargarMaterialDifusion();
    }
  }, [form.id]);

  const cargarMaterialDifusion = async () => {
    setLoadingData(true);
    try {
      const response = await RestPrivateAntesala({
        url: `/evento-imagenes?evento_id=${form.id[0]}`,
        method: "get",
        setLoading: () => { }
      });


      if (response.status === 200 && response.data.data) {
        const materialesFiltrados = response.data.data.filter(
          (item) => item.descripcion === "material de difusion"
        );
        setMaterialDifusion(materialesFiltrados);
      }
    } catch (error) {
      console.error("Error al cargar material de difusión:", error);
      Alerts({
        msg: "Error",
        title: "No se pudo cargar el material de difusión",
        code: "500",
        type: "error",
      });
    } finally {
      setLoadingData(false);
    }
  };

  // Exponer función para subir material pendiente
  useImperativeHandle(ref, () => ({
    subirMaterialPendiente: async (eventoId, files) => {
      if (!files || files.length === 0) {
        return; // Material de difusión opcional
      }

      if (files.length > 2) {
        throw new Error("No puedes subir más de 2 imágenes de difusión");
      }

      setUploadingMaterial(true);

      for (const file of files) {
        const formData = new FormData();
        formData.append("evento_id", eventoId);
        formData.append("imagen", file);
        formData.append("descripcion", "material de difusion");

        try {
          await RestPrivateAntesala({
            url: "/evento-imagenes",
            formData: formData,
            method: "post",
            setLoading: () => { },
          });
        } catch (error) {
          console.error("Error al subir material pendiente:", error);
        }
      }

      setUploadingMaterial(false);
    },
    validarMaterialDifusion: () => {
      const total = form.id && form.id[0] ? materialDifusion.length : materialPendiente.length;
      if (total > 2) {
        return { valid: false, message: "No puedes subir más de 2 imágenes de difusión" };
      }
      return { valid: true, message: "" };
    }
  }));

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length === 0) {
      return Alerts({
        msg: "Warning",
        title: "Solo se permiten archivos de imagen",
        code: "400",
        type: "error",
      });
    }

    await subirImagenesMaterial(imageFiles);
  };

  const handleFileInputChange = async (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length === 0) {
      return Alerts({
        msg: "Warning",
        title: "Solo se permiten archivos de imagen",
        code: "400",
        type: "error",
      });
    }

    await subirImagenesMaterial(imageFiles);
  };

  const subirImagenesMaterial = async (files) => {
    // Validar límite de 2 archivos
    const totalActual = form.id && form.id[0] ? materialDifusion.length : materialPendiente.length;
    const totalNuevo = totalActual + files.length;

    if (totalNuevo > 2) {
      return Alerts({
        msg: "Warning",
        title: `Solo puedes subir un máximo de 2 archivos. Actualmente tienes ${totalActual}.`,
        code: "400",
        type: "error",
      });
    }

    // Si el evento no existe, guardar archivos como pendientes
    if (!form.id || !form.id[0]) {
      setMaterialPendiente((prev) => [...prev, ...files]);
      Alerts({
        msg: "Info",
        title: `${files.length}`,
        code: "200",
        type: "success",
      });
      return;
    }

    // Si el evento ya existe, subir directamente
    setUploadingMaterial(true);

    for (const file of files) {
      const formData = new FormData();
      formData.append("evento_id", form.id[0]);
      formData.append("archivo", file);
      formData.append("descripcion", "material de difusion");

      try {
        const response = await RestPrivateAntesala({
          url: "/evento-imagenes",
          formData: formData,
          method: "post",
          setLoading: setUploadingMaterial,
        });

        if (response.status === 200 || response.status === 201) {
          Alerts({
            msg: "Success",
            title: "Imagen subida correctamente",
            code: "200",
            type: "success",
          });
        } else {
          Alerts({
            msg: "Error",
            title: response.data.msg || "Error al subir la imagen",
            code: "400",
            type: "error",
          });
        }
      } catch (error) {
        Alerts({
          msg: "Error",
          title: "Error al subir la imagen",
          code: "400",
          type: "error",
        });
      }
    }

    await cargarMaterialDifusion();
  };

  const eliminarMaterialDifusion = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta imagen?")) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("_method", "DELETE");

      const response = await RestPrivateAntesala({
        url: `/evento-imagen/${id}`,
        formData: formData,
        method: "post",
      });

      if (response.status === 200) {
        Alerts({
          msg: "Success",
          title: "Imagen eliminada correctamente",
          code: "200",
          type: "success",
        });
        await cargarMaterialDifusion();
      } else {
        Alerts({
          msg: "Error",
          title: response.data.msg || "Error al eliminar la imagen",
          code: "400",
          type: "error",
        });
      }
    } catch (error) {
      Alerts({
        msg: "Error",
        title: "Error al eliminar la imagen",
        code: "400",
        type: "error",
      });
    }
  };

  const descargarImagen = async (url, nombre) => {
    try {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
    } catch (error) {
      console.error('Error al descargar la imagen:', error);
      Alerts({
        msg: "Error",
        title: "Error al descargar la imagen",
        code: "400",
        type: "error",
      });
    }
  };

  const onClick = (e, value) => {
    switch (e.target.name) {
      case "ficha_tecnica-delete":
        {
          let input = form;
          let result = input.ficha_tecnica[0];

          delete result[value];
          result = result.filter((i) => i != null);

          setForm({
            ...form,
            ficha_tecnica: [result, "required"],
          });

          Alerts({
            msg: "Success",
            title: "Participante eliminado correctamente",
            code: "200",
            type: "success",
          });
        }
        break;
      case "ficha_tecnica":
        {
          const input_validation = Validate(
            form,
            JSON.parse(JSON.stringify(VALIDATION_FORM_EVENTO)),
            "ficha_tecnica"
          );
          setValidation(input_validation);
          //Validaciones
          if (input_validation.error.status) {
            return Alerts({
              msg: "Warning",
              title: input_validation.error.msg,
              code: "201",
              type: "error",
            });
          }

          let input = form;
          let result = input[e.target.name][0];
          result.push({
            nombre: form["ficha_tecnica-nombre"][0],
            rol: form["ficha_tecnica-rol"][0],
          });

          input[e.target.name][0] = result;

          setForm({
            ...form,
            [e.target.name]: input[e.target.name],
            "ficha_tecnica-nombre": ["", "required"],
            "ficha_tecnica-rol": ["", "required"],
          });

          Alerts({
            msg: "Success",
            title: "Participante agregado correctamente",
            code: "200",
            type: "success",
          });
        }
        break;
      case "link":
        {
          // Validar formato de URL
          const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
          if (!urlPattern.test(form["link-url"][0])) {
            return Alerts({
              msg: "Warning",
              title: "Por favor ingresa una URL válida",
              code: "400",
              type: "error",
            });
          }

          const input_validation = Validate(form, validation, "link");
          setValidation(input_validation);
          //Validaciones
          if (input_validation.error.status) {
            return Alerts({
              msg: "Warning",
              title: input_validation.error.msg,
              code: "201",
              type: "error",
            });
          }

          let input = form;
          let result = input[e.target.name][0];
          result.push({
            nombre: form["link-nombre"][0],
            url: form["link-url"][0],
          });

          input[e.target.name][0] = result;

          setForm({
            ...form,
            [e.target.name]: input[e.target.name],
            "link-nombre": ["", "required"],
            "link-url": ["", "required"],
          });

          Alerts({
            msg: "Success",
            title: "Link agregado correctamente",
            code: "200",
            type: "success",
          });
        }
        break;
      case "link-delete":
        {
          let input = form;
          let result = input.link[0];

          delete result[value];
          result = result.filter((i) => i != null);

          setForm({
            ...form,
            link: [result, "required"],
          });

          Alerts({
            msg: "Success",
            title: "Link eliminado correctamente",
            code: "200",
            type: "success",
          });
        }
        break;
    }
  };

  // Handlers para drag and drop de imagen principal
  const handleDragOverPrincipal = (e) => {
    e.preventDefault();
    setIsDraggingPrincipal(true);
  };

  const handleDragLeavePrincipal = (e) => {
    e.preventDefault();
    setIsDraggingPrincipal(false);
  };

  const handleDropPrincipal = (e) => {
    e.preventDefault();
    setIsDraggingPrincipal(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find((file) => file.type.startsWith("image/"));

    if (!imageFile) {
      return Alerts({
        msg: "Warning",
        title: "Solo se permiten archivos de imagen",
        code: "400",
        type: "error",
      });
    }

    if (files.length > 1) {
      Alerts({
        msg: "Info",
        title: "Solo puedes subir una imagen principal. Se tomará la primera.",
        code: "200",
        type: "info",
      });
    }

    procesarImagenPrincipal(imageFile);
  };

  const procesarImagenPrincipal = (file) => {
    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      return Alerts({
        msg: "Warning",
        title: "La imagen no debe superar los 5MB",
        code: "400",
        type: "error",
      });
    }

    let input = form;
    input["imagen"][0] = file;
    input["imagen_url"][0] = URL.createObjectURL(file);

    // Limpiar validación de la imagen
    setValidation({
      ...validation,
      imagen: {
        status: false,
        msg: "",
      },
      imagen_url: {
        status: false,
        msg: "",
      },
    });

    setForm({
      ...form,
      imagen: input["imagen"],
      imagen_url: input["imagen_url"],
    });

    Alerts({
      msg: "Success",
      title: "Imagen principal cargada correctamente",
      code: "200",
      type: "success",
    });
  };

  // Función para generar el slug
  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .normalize("NFD") // Normalizar caracteres Unicode
      .replace(/[\u0300-\u036f]/g, "") // Eliminar diacríticos
      .replace(/[^a-z0-9\s-]/g, "") // Eliminar caracteres especiales
      .trim()
      .replace(/\s+/g, "-") // Reemplazar espacios por guiones
      .replace(/-+/g, "-"); // Eliminar guiones duplicados
  };

  const onChange = (e, value) => {
    switch (e.target.name) {
      case "atp":
      case "adultos":
      case "infancias": {
        // Crear una copia profunda para evitar mutaciones
        const nuevoValor = form[e.target.name][0] === 'SI' ? 'NO' : 'SI';

        // Actualizar el form con el nuevo valor
        const nuevoForm = {
          ...form,
          [e.target.name]: [nuevoValor, form[e.target.name][1]],
        };

        // Verificar si al menos uno de los tipos de público está seleccionado
        const tienePublicoSeleccionado =
          nuevoForm.atp[0] === 'SI' ||
          nuevoForm.adultos[0] === 'SI' ||
          nuevoForm.infancias[0] === 'SI';

        // Limpiar validación de grupo si hay al menos uno seleccionado
        if (tienePublicoSeleccionado) {
          setValidation({
            ...validation,
            grupo: {
              status: false,
              msg: "",
            },
          });
        }

        setForm(nuevoForm);
      } break;
      case "nombre":
        {
          // Validar que no contenga "/" ni símbolos extraños
          const value = e.target.value;
          const invalidCharsRegex = /[\/\\<>:"|?*\x00-\x1F]/g;

          if (invalidCharsRegex.test(value)) {
            return Alerts({
              msg: "Warning",
              title: "El nombre del evento no puede contener los caracteres: / \\ < > : \" | ? *",
              code: "400",
              type: "error",
            });
          }

          let input = form;
          input[e.target.name][0] = value;
          // Generar slug automáticamente
          input["slug"][0] = generateSlug(value);

          // Limpiar validación del campo si tiene contenido
          if (value.trim() !== "") {
            setValidation({
              ...validation,
              [e.target.name]: {
                status: false,
                msg: "",
              },
            });
          }

          setForm({
            ...form,
            [e.target.name]: input[e.target.name],
            slug: input["slug"],
          });
        }
        break;
      case "slug":
        {
          // Permitir edición manual del slug con validación
          const value = e.target.value;
          const slugValue = generateSlug(value);

          let input = form;
          input[e.target.name][0] = slugValue;

          // Limpiar validación del campo si tiene contenido
          if (slugValue.trim() !== "") {
            setValidation({
              ...validation,
              [e.target.name]: {
                status: false,
                msg: "",
              },
            });
          }

          setForm({
            ...form,
            [e.target.name]: input[e.target.name],
          });
        }
        break;
      case "duracion":
        {
          let input = form;
          input[e.target.name][0] = e.target.value;

          // Limpiar validación del campo si tiene contenido válido (número mayor a 0)
          if (e.target.value && Number(e.target.value) > 0) {
            setValidation({
              ...validation,
              [e.target.name]: {
                status: false,
                msg: "",
              },
            });
          }

          setForm({
            ...form,
            [e.target.name]: input[e.target.name],
          });
        }
        break;
      case "nombre_grupo":
      case "clase":
      case "grupo":
      case "link-nombre":
      case "link-url":
      case "ficha_tecnica-nombre":
      case "ficha_tecnica-rol":
      case "meta_pixel_code":
        {
          let input = form;
          // Inicializar el campo si no existe
          if (!input[e.target.name]) {
            input[e.target.name] = ["", ""];
          }
          input[e.target.name][0] = e.target.value;

          // Limpiar validación del campo si tiene contenido
          if (e.target.value && e.target.value.trim() !== "") {
            setValidation({
              ...validation,
              [e.target.name]: {
                status: false,
                msg: "",
              },
            });
          }

          setForm({
            ...form,
            [e.target.name]: input[e.target.name],
          });
        }
        break;
      case "imagen":
        {
          const file = e.target.files[0];
          if (file) {
            procesarImagenPrincipal(file);
          }
        }
        break;
      case "categorias":
        {
          let result = [...form.categorias[0]]; // Crear copia del array
          const categoriaId = categorias[e.target.id].id;
          const index = result.indexOf(categoriaId);

          if (index < 0 && result.length < 3) {
            // Agregar categoría
            result.push(categoriaId);
          } else if (index >= 0) {
            // Remover categoría
            result.splice(index, 1);
          }

          // Limpiar validación de categorías si hay al menos una seleccionada
          if (result.length > 0) {
            setValidation({
              ...validation,
              categorias: {
                status: false,
                msg: "",
              },
            });
          }

          // Actualizar form después de limpiar validación
          setForm({
            ...form,
            categorias: [result, "required"],
          });
        }
        break;
    }
  };


  const handleChange = (value) => {
    // Validar que no contenga emojis
    const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{FE00}-\u{FE0F}\u{1F004}\u{1F0CF}\u{1F18E}\u{3030}\u{2B50}\u{2B55}\u{231A}-\u{231B}\u{23E9}-\u{23EC}\u{23F0}\u{23F3}\u{25FD}-\u{25FE}\u{2614}-\u{2615}\u{2648}-\u{2653}\u{267F}\u{2693}\u{26A1}\u{26AA}-\u{26AB}\u{26BD}-\u{26BE}\u{26C4}-\u{26C5}\u{26CE}\u{26D4}\u{26EA}\u{26F2}-\u{26F3}\u{26F5}\u{26FA}\u{26FD}\u{2705}\u{270A}-\u{270B}\u{2728}\u{274C}\u{274E}\u{2753}-\u{2755}\u{2757}\u{2795}-\u{2797}\u{27B0}\u{27BF}\u{2B1B}-\u{2B1C}]/gu;

    if (emojiRegex.test(value)) {
      Alerts({
        msg: "Warning",
        title: "No se permiten emojis en la descripción del evento",
        code: "400",
        type: "error",
      });
      return;
    }

    let input = form;
    input['descripcion'][0] = value;

    // Limpiar validación de la descripción si tiene contenido
    if (value.trim() !== "") {
      setValidation({
        ...validation,
        descripcion: {
          status: false,
          msg: "",
        },
      });
    }

    setForm({
      ...form,
      descripcion: input['descripcion'],
    });
  }

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        video: function () {
          const url = prompt('Ingresa la URL del video de YouTube:');
          if (url) {
            let embedUrl = url;

            // Convertir URL de YouTube a formato embed
            if (url.includes('youtube.com/watch?v=')) {
              const videoId = url.split('v=')[1].split('&')[0];
              embedUrl = `https://www.youtube.com/embed/${videoId}`;
            } else if (url.includes('youtu.be/')) {
              const videoId = url.split('youtu.be/')[1].split('?')[0];
              embedUrl = `https://www.youtube.com/embed/${videoId}`;
            }

            const range = this.quill.getSelection(true);
            this.quill.insertEmbed(range.index, 'video', embedUrl);
            this.quill.setSelection(range.index + 1);
          }
        }
      }
    }
  }), []);


  return (
    <div className="container-md w-100 tw-space-y-8">
      {/* Modal de previsualización de imagen */}
      {imagePreviewModal && (
        <div
          className="modal fade show d-block tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center"
          style={{ backgroundColor: "rgba(0,0,0,0.8)" }}
          onClick={() => setImagePreviewModal(null)}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg tw-relative tw-w-full tw-max-w-4xl">
            <div className="modal-content tw-bg-transparent tw-border-0 tw-rounded-2xl">
              <div className="modal-body tw-p-0 tw-relative">
                <button
                  className="btn btn-close btn-close-white tw-absolute tw-top-4 tw-right-4 tw-z-10 tw-transition-transform hover:tw-scale-110"
                  style={{ zIndex: 1000 }}
                  onClick={() => setImagePreviewModal(null)}
                ></button>
                <img
                  src={imagePreviewModal}
                  alt="Vista previa"
                  className="tw-w-full tw-rounded-2xl tw-shadow-2xl"
                  style={{ maxHeight: "90vh", objectFit: "contain" }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="tw-bg-white tw-rounded-2xl tw-p-6 tw-shadow-sm"
      >
        <div className="row">
          <div className="col-md-12 mb-4">
            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-6">
              <div className="tw-w-10 tw-h-10 tw-rounded-full bg-dark tw-flex tw-items-center tw-justify-center">
                <i className="bi bi-info-circle text-white tw-text-xl"></i>
              </div>
              <div className="tw-flex-1">
                <h5 className="GothamBold tw-text-xl tw-font-bold tw-text-gray-800 tw-mb-1">
                  Información del evento
                </h5>
                <p className="tw-text-sm tw-text-gray-500 tw-mb-0">Datos básicos y esenciales del evento</p>
              </div>
              <span className="tw-px-3 tw-py-1 tw-text-xs tw-bg-red-100 tw-text-red-700 tw-rounded-full tw-font-semibold">Obligatorio</span>
            </div>

            <div className="row">
              {/* Preview de la imagen principal */}
              <div className="col-md-5 mb-3">
                <div
                  className="position-relative overflow-hidden tw-rounded-xl tw-transition-all tw-duration-300"
                  style={{
                    height: "350px",
                    border: form.imagen_url[0] ? "2px solid #e5e7eb" : "2px dashed #e5e7eb",
                    transition: "all 0.3s ease",
                    backgroundColor: "#f9fafb"
                  }}
                >
                  {form.imagen_url[0] ? (
                    <>
                      <img
                        src={form.imagen_url[0]}
                        alt="Vista previa de imagen principal"
                        className="w-100 h-100 tw-object-cover tw-transition-transform tw-duration-500 hover:tw-scale-110"
                        style={{ objectFit: "cover" }}
                      />
                      {/* Overlay con acciones */}
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center tw-bg-gradient-to-b tw-from-black/30 tw-to-black/70 tw-opacity-0 hover:tw-opacity-100 tw-transition-opacity tw-duration-300"
                      >
                        <div className="text-center tw-space-y-3">
                          <button
                            className="btn btn-light btn-lg me-2 mb-2 tw-transform tw-transition-all tw-duration-200 hover:tw-scale-110 hover:tw-shadow-2xl"
                            onClick={() => setImagePreviewModal(form.imagen_url[0])}
                            type="button"
                          >
                            <i className="bi bi-zoom-in me-2 tw-text-lg"></i>
                            Ampliar
                          </button>
                          <label className="btn btn-primary btn-lg mb-2 tw-transform tw-transition-all tw-duration-200 hover:tw-scale-110 hover:tw-shadow-2xl tw-bg-blue-600 hover:tw-bg-blue-700" style={{ cursor: "pointer" }}>
                            <i className="bi bi-arrow-repeat me-2 tw-text-lg"></i>
                            Cambiar
                            <input
                              type="file"
                              className="d-none"
                              accept="image/*"
                              name="imagen"
                              onChange={onChange}
                            />
                          </label>
                        </div>
                      </div>
                      {/* Badge de estado */}
                      <div
                        className="position-absolute top-0 end-0 m-3"
                      >
                        <span className="badge bg-success tw-bg-green-500 tw-shadow-lg tw-animate-pulse" style={{ fontSize: "0.85rem", padding: "8px 12px" }}>
                          <i className="bi bi-check-circle me-1"></i>
                          Imagen cargada
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                      <div className="text-center tw-animate-pulse">
                        <i className="bi bi-image tw-text-7xl tw-text-gray-300 tw-mb-4"></i>
                        <p className="mt-3 mb-0 tw-text-gray-500 tw-font-semibold">Sin imagen</p>
                        <small className="tw-text-gray-400">Arrastra una imagen o usa el selector</small>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Zona de drag and drop */}
              <div className="col-md-7">
                <label className="tw-flex tw-items-center tw-gap-2 tw-mb-3 tw-text-sm tw-font-medium tw-text-gray-700">
                  <i className="bi bi-image tw-text-blue-600"></i>
                  Imagen principal <span className="tw-text-red-500">*</span>
                  <span className="tw-text-xs tw-text-gray-500 tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded">
                    1:1 (1080 x 1080 px)
                  </span>
                </label>

                <div
                  onDragOver={handleDragOverPrincipal}
                  onDragLeave={handleDragLeavePrincipal}
                  onDrop={handleDropPrincipal}
                  className={`tw-rounded-xl tw-transition-all tw-duration-200 ${
                    isDraggingPrincipal
                      ? "tw-border-blue-400 tw-bg-blue-50"
                      : "tw-border-gray-200 hover:tw-border-blue-300 hover:tw-bg-gray-50"
                  }`}
                  style={{
                    border: "2px dashed",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    minHeight: "200px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "1.5rem"
                  }}
                >
                  <input
                    type="file"
                    id="imagen-principal-input"
                    accept="image/*"
                    name="imagen"
                    onChange={onChange}
                    style={{ display: "none" }}
                  />
                  <label
                    htmlFor="imagen-principal-input"
                    className="w-100 h-100 d-flex flex-column align-items-center justify-content-center tw-cursor-pointer"
                    style={{ cursor: "pointer" }}
                  >
                    {isDraggingPrincipal ? (
                      <div className="tw-animate-bounce">
                        <i className="bi bi-cloud-upload-fill text-dark tw-text-7xl tw-text-blue-600 tw-mb-4"></i>
                        <p className="mt-3 mb-0 text-dark fw-bold tw-text-xl tw-text-blue-700 tw-font-bold">¡Suelta la imagen aquí!</p>
                      </div>
                    ) : (
                      <div className="tw-transition-all tw-duration-300 hover:tw-scale-105">
                        <i className="bi bi-cloud-upload text-secondary tw-text-7xl tw-text-gray-400 tw-mb-3 tw-block"></i>
                        <p className="mt-3 mb-2 text-dark fw-bold tw-text-lg tw-text-gray-700 tw-font-semibold">
                          Arrastra tu imagen aquí
                        </p>
                        <p className="tw-text-sm tw-text-gray-500 tw-mb-2">o haz clic para seleccionar</p>
                        <small className="text-muted mt-3 d-block tw-px-4 tw-py-2 tw-bg-gray-100 tw-rounded-full tw-text-xs tw-text-gray-600">
                          Formatos: JPG, PNG, GIF · Máx. 5MB
                        </small>
                      </div>
                    )}
                  </label>
                </div>

                {/* Mensaje de validación */}
                {validation.imagen_url.status && (
                  <div className="tw-mt-3 tw-p-3 tw-bg-red-50 tw-rounded-lg tw-text-sm">
                    <i className="bi bi-exclamation-circle tw-text-red-600 tw-mr-2"></i>
                    <span className="tw-text-red-700">{validation.imagen_url.msg}</span>
                  </div>
                )}

                {/* Información adicional */}
                <div className="tw-mt-4 tw-p-4 tw-bg-blue-50 tw-rounded-lg">
                  <h6 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center">
                    <i className="bi bi-lightbulb tw-text-yellow-600 tw-mr-2"></i>
                    Recomendaciones
                  </h6>
                  <ul className="tw-space-y-2 tw-text-xs tw-text-gray-600 tw-mb-0 tw-pl-0" style={{ listStyle: "none" }}>
                    <li className="tw-flex tw-items-start">
                      <i className="bi bi-check-circle-fill tw-text-green-500 tw-mr-2 tw-mt-0.5 tw-text-xs"></i>
                      <span>Usa imágenes de alta calidad y bien iluminadas</span>
                    </li>
                    <li className="tw-flex tw-items-start">
                      <i className="bi bi-check-circle-fill tw-text-green-500 tw-mr-2 tw-mt-0.5 tw-text-xs"></i>
                      <span>Formato cuadrado 1:1 (1080 x 1080 px)</span>
                    </li>
                    <li className="tw-flex tw-items-start">
                      <i className="bi bi-check-circle-fill tw-text-green-500 tw-mr-2 tw-mt-0.5 tw-text-xs"></i>
                      <span>Evita texto o logos muy pequeños</span>
                    </li>
                    <li className="tw-flex tw-items-start">
                      <i className="bi bi-check-circle-fill tw-text-green-500 tw-mr-2 tw-mt-0.5 tw-text-xs"></i>
                      <span>Tamaño máximo: 5MB</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-8 col-12 mb-4">
              <label className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2 tw-flex tw-items-center">
                <i className="bi bi-card-heading text-dark tw-mr-2"></i>
                Nombre del evento <span className="tw-text-red-500 tw-ml-1">*</span>
              </label>
              <input
                type="text"
                className={`form-control tw-rounded-lg tw-border tw-px-4 tw-py-3 tw-transition-all focus:tw-ring-2 focus:tw-ring-blue-100 focus:tw-border-blue-400 ${validation.nombre.status ? 'is-invalid tw-border-red-300' : 'tw-border-gray-200'}`}
                name="nombre"
                value={form.nombre[0]}
                onChange={onChange}
                placeholder="Ej: La casa de Bernarda Alba"
              />
              <small className="tw-text-xs tw-text-gray-500 tw-mt-1 tw-block">
                No se permiten los caracteres: / \ &lt; &gt; : " | ? *
              </small>
              {validation.nombre.status && (
                <div className="tw-mt-2 tw-text-sm tw-text-red-600">
                  {validation.nombre.msg}
                </div>
              )}
            </div>

            <div className="col-md-8 col-12 mb-4">
              <label className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2 tw-flex tw-items-center">
                <i className="bi bi-link-45deg text-dark tw-mr-2"></i>
                Slug (URL amigable) <span className="tw-text-red-500 tw-ml-1">*</span>
              </label>
              <input
                type="text"
                className={`form-control tw-rounded-lg tw-border tw-px-4 tw-py-3 tw-transition-all focus:tw-ring-2 focus:tw-ring-purple-100 focus:tw-border-purple-400 tw-font-mono tw-text-sm ${validation.slug?.status ? 'is-invalid tw-border-red-300' : 'tw-border-gray-200'}`}
                name="slug"
                value={form.slug?.[0] || ''}
                onChange={onChange}
                placeholder="la-casa-de-bernarda-alba"
              />
              <small className="tw-text-xs tw-text-gray-500 tw-mt-1 tw-block tw-flex tw-items-center">
                <i className="bi bi-info-circle tw-mr-1"></i>
                Se genera automáticamente desde el nombre. Solo letras, números y guiones.
              </small>
              {validation.slug?.status && (
                <div className="tw-mt-2 tw-text-sm tw-text-red-600">
                  {validation.slug.msg}
                </div>
              )}
            </div>

            <div className="col-md-8 col-12 mb-3">
              <Input
                onChange={onChange}
                label={"Nombre del grupo (opcional)"}
                name={"nombre_grupo"}
                type={"text"}
                value={form.nombre_grupo[0]}
                svg={""}
                validate={validation.nombre_grupo}
                disabled={false}
              />
            </div>

            <div className="col-md-4 col-12 mb-3">
              <Input
                onChange={onChange}
                label={"Duracion del evento en minutos"}
                name={"duracion"}
                type={"number"}
                value={form.duracion[0]}
                svg={""}
                validate={validation.duracion}
                disabled={false}
              />
            </div>

            <div className="col-md-4 col-12 mb-4">
              <label className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2 tw-flex tw-items-center tw-justify-between">
                <div className="tw-flex tw-items-center">
                  <i className="bi bi-tags text-dark tw-mr-2"></i>
                  Categorías <span className="tw-text-red-500 tw-ml-1">*</span>
                </div>
                <span className="tw-text-xs text-white bg-dark tw-px-2 tw-py-1 tw-rounded">
                  Máximo. 3
                </span>
              </label>
              <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 tw-bg-gray-50" style={{ maxHeight: "200px", overflowY: "auto" }}>
                {categorias
                  ? categorias.map((c, index) => (
                    <div key={index} className="tw-py-1.5">
                      <CheckBox
                        onChange={onChange}
                        label={c.nombre}
                        name={"categorias"}
                        type={"checkbox"}
                        checked={
                          form.categorias[0].indexOf(c.id) < 0 ? false : true
                        }
                        uniqueKey={index}
                        disabled={form.categorias[0].length >= 3 && form.categorias[0].indexOf(c.id) < 0}
                      />
                    </div>
                  ))
                  : <div className="tw-text-center tw-text-gray-500 tw-py-3 tw-text-sm">
                      <i className="bi bi-hourglass-split tw-animate-spin tw-mr-2"></i> Cargando categorías...
                    </div>}
                <div className="tw-text-right tw-mt-3">
                  <small className={`tw-text-xs tw-px-2 tw-py-1 tw-rounded ${form.categorias[0].length === 0 ? 'tw-bg-red-100 tw-text-red-700' : form.categorias[0].length >= 3 ? 'tw-bg-green-100 tw-text-green-700' : 'tw-bg-yellow-100 tw-text-yellow-700'}`}>
                    {form.categorias[0].length} / 3 seleccionadas
                  </small>
                </div>
              </div>

              {validation.categorias.status && (
                <div className="tw-mt-2 tw-text-sm tw-text-red-600">
                  {validation.categorias.msg}
                </div>
              )}
            </div>

            <div className="col-md-8 col-12 mb-4">
              <label className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2 tw-flex tw-items-center tw-justify-between">
                <div className="tw-flex tw-items-center">
                  <i className="bi bi-facebook tw-text-blue-600 tw-mr-2"></i>
                  Pixel de Meta
                </div>
                {form.meta_pixel_code?.[0] && form.meta_pixel_code[0].trim() !== '' && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger tw-flex tw-items-center tw-gap-1"
                    onClick={() => {
                      setForm({
                        ...form,
                        meta_pixel_code: ["", ""]
                      });
                    }}
                    title="Eliminar pixel code"
                  >
                    <i className="bi bi-trash"></i>
                    <span className="tw-text-xs">Eliminar</span>
                  </button>
                )}
              </label>
              <div className="tw-relative">
                <input
                  type="text"
                  className={`form-control tw-rounded-lg tw-border tw-px-4 tw-py-3 tw-transition-all focus:tw-ring-2 focus:tw-ring-blue-100 focus:tw-border-blue-400 ${
                    form.meta_pixel_code?.[0] && form.meta_pixel_code[0].trim() !== ''
                      ? 'tw-border-blue-400 tw-bg-blue-50'
                      : 'tw-border-gray-200'
                  }`}
                  name="meta_pixel_code"
                  value={form.meta_pixel_code?.[0] || ''}
                  onChange={onChange}
                  placeholder="Código de pixel de Meta (opcional)"
                  maxLength={300}
                />
                {form.meta_pixel_code?.[0] && form.meta_pixel_code[0].trim() !== '' && (
                  <div className="tw-absolute tw-right-3 tw-top-1/2 tw-transform tw--translate-y-1/2">
                    <i className="bi bi-check-circle-fill tw-text-green-500"></i>
                  </div>
                )}
              </div>
              <small className="tw-text-xs tw-text-gray-500 tw-mt-1 tw-block tw-flex tw-items-center">
                <i className="bi bi-info-circle tw-mr-1"></i>
                Código de seguimiento de Meta/Facebook Pixel (máximo 300 caracteres)
              </small>
            </div>

            <div className="col-md-4 col-12 mb-4">
              <label className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2 tw-flex tw-items-center">
                <i className="bi bi-people text-dark tw-mr-2"></i>
                Tipo de público <span className="tw-text-red-500 tw-ml-1">*</span>
              </label>
              <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 tw-bg-gray-50 tw-space-y-2">
                <div class="form-check tw-flex tw-items-center">
                  <input
                    class="form-check-input tw-cursor-pointer"
                    type="checkbox"
                    name="atp"
                    id="atp-check"
                    value={"ATP"}
                    checked={form.atp[0] == "SI" ? true : false}
                    onChange={(event) => onChange(event)}
                  />
                  <label class="form-check-label tw-ml-2 tw-cursor-pointer tw-text-sm tw-text-gray-700" for="atp-check">
                    ATP (Apto Todo Público)
                  </label>
                </div>
                <div class="form-check tw-flex tw-items-center">
                  <input
                    class="form-check-input tw-cursor-pointer"
                    type="checkbox"
                    name="adultos"
                    id="adultos-check"
                    value={"ADULTOS"}
                    checked={form.adultos[0] == "SI" ? true : false}
                    onChange={(event) => onChange(event)}
                  />
                  <label class="form-check-label tw-ml-2 tw-cursor-pointer tw-text-sm tw-text-gray-700" for="adultos-check">
                    Adultos
                  </label>
                </div>
                <div class="form-check tw-flex tw-items-center">
                  <input
                    class="form-check-input tw-cursor-pointer"
                    type="checkbox"
                    name="infancias"
                    id="infancias-check"
                    value={"INFANCIAS"}
                    checked={form.infancias[0] == "SI" ? true : false}
                    onChange={(event) => onChange(event)}
                  />
                  <label class="form-check-label tw-ml-2 tw-cursor-pointer tw-text-sm tw-text-gray-700" for="infancias-check">
                    Infancias
                  </label>
                </div>
              </div>
              {validation.grupo.status && (
                <div className="tw-mt-2 tw-text-sm tw-text-red-600">
                  {validation.grupo.msg}
                </div>
              )}
            </div>

            <div className="col-md-4 col-12 mb-3 px-md-5 text-nowrap d-none">
              Tipo de evento
              <div class="form-check">
                <label class="form-check-label" for="flexRadioDefault1">
                  ONLINE
                  <input
                    class="form-check-input"
                    type="radio"
                    name="clase"
                    value={"ONLINE"}
                    id="flexRadioDefault1"
                    checked={form.clase[0] == "ONLINE" ? true : false}
                    onChange={(event) => onChange(event)}
                  />
                </label>
              </div>
              <div class="form-check">
                <label class="form-check-label" for="flexRadioDefault1">
                  PRESENCIAL
                  <input
                    class="form-check-input"
                    type="radio"
                    name="clase"
                    value={"PRESENCIAL"}
                    id="flexRadioDefault1"
                    checked={form.clase[0] == "PRESENCIAL" ? true : false}
                    onChange={(event) => onChange(event)}
                  />
                </label>
              </div>
              {validation.clase.status ? (
                <div className="text-danger">{validation.clase.msg}</div>
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="tw-bg-white tw-rounded-2xl tw-p-6 tw-shadow-sm">
        <div className="row">
          <div className="col-md-12 mb-4">
            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-6">
              <div className="tw-w-10 tw-h-10 tw-rounded-full bg-dark tw-flex tw-items-center tw-justify-center">
                <i className="bi bi-file-text text-white tw-text-xl"></i>
              </div>
              <div className="tw-flex-1">
                <h5 className="GothamBold tw-text-xl tw-font-bold tw-text-gray-800 tw-mb-1">
                  Describe tu evento
                </h5>
                <p className="tw-text-sm tw-text-gray-500 tw-mb-0">Proporciona una descripción detallada y atractiva</p>
              </div>
              <span className="tw-px-3 tw-py-1 tw-text-xs tw-bg-red-100 tw-text-red-700 tw-rounded-full tw-font-semibold">Obligatorio</span>
            </div>
            <div style={{ minHeight: "300px" }} className="tw-rounded-lg tw-overflow-hidden tw-border tw-border-gray-200">
              <ReactQuill
                value={form?.descripcion[0]}
                onChange={handleChange}
                theme="snow"
                modules={modules}
                placeholder="Escribe aquí la descripción del evento..."
              />
            </div>
            <div className="tw-mt-3 tw-flex tw-justify-between tw-items-center">
              <small className="tw-text-xs tw-text-gray-500">
                <i className="bi bi-info-circle tw-mr-1"></i>
                No se permiten emojis en la descripción
              </small>
              {form?.descripcion[0] && (
                <small className={`tw-text-xs tw-px-2 tw-py-1 tw-rounded ${form.descripcion[0].length > 100 ? 'tw-bg-green-100 tw-text-green-700' : 'tw-bg-yellow-100 tw-text-yellow-700'}`}>
                  {form.descripcion[0].length > 100 ? '✓ Descripción completa' : 'Agrega más detalles'}
                </small>
              )}
            </div>
          </div>
          {validation.descripcion.status && (
            <div className="tw-mt-2 tw-text-sm tw-text-red-600">
              {validation.descripcion.msg}
            </div>
          )}
        </div>
      </div>

      <div className="tw-bg-white tw-rounded-2xl tw-p-6 tw-shadow-sm">
        <div className="row">
          <div className="col-md-6 col-12 mb-4">
            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
              <div className="tw-w-10 tw-h-10 tw-rounded-full bg-dark tw-flex tw-items-center tw-justify-center">
                <i className="bi bi-person-lines-fill bg-dark text-white tw-text-xl"></i>
              </div>
              <div className="tw-flex-1">
                <h5 className="GothamBold tw-text-xl tw-font-bold tw-text-gray-800 tw-mb-1">
                  Ficha técnica
                </h5>
                <p className="tw-text-sm tw-text-gray-500 tw-mb-0">Participantes del evento</p>
              </div>
            </div>
            <Input
              onChange={onChange}
              label={"Nombre de participante"}
              name={"ficha_tecnica-nombre"}
              type={"text"}
              value={form["ficha_tecnica-nombre"][0]}
              icon={'<i class="bi bi-person"></i>'}
              validate={validation["ficha_tecnica-nombre"]}
              disabled={false}
            />
            <Input
              onChange={onChange}
              label={"Rol de participante"}
              name={"ficha_tecnica-rol"}
              type={"text"}
              value={form["ficha_tecnica-rol"][0]}
              icon={'<i class="bi bi-briefcase"></i>'}
              validate={validation["ficha_tecnica-rol"]}
              disabled={false}
            />
            <button
              className="btn btn-primary tw-w-full tw-py-3 tw-font-medium"
              onClick={(event) => {
                event.target.name = "ficha_tecnica";
                onClick(event);
              }}
            >
              <i className="bi bi-plus-circle me-2"></i>
              Añadir participante
            </button>
            {validation.ficha_tecnica.status && (
              <div className="tw-mt-2 tw-text-sm tw-text-red-600">
                {validation.ficha_tecnica.msg}
              </div>
            )}
          </div>
          <div className="col-md-6">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
              <h6 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-0">
                Participantes ({form.ficha_tecnica[0].length})
              </h6>
            </div>
            {form.ficha_tecnica[0].length === 0 ? (
              <div className="tw-text-center tw-py-12 tw-border tw-border-dashed tw-border-gray-200 tw-rounded-lg tw-bg-gray-50">
                <i className="bi bi-person-plus tw-text-5xl tw-text-gray-300 tw-block tw-mb-3"></i>
                <p className="tw-text-sm tw-text-gray-500 tw-mb-0">Aún no hay participantes agregados</p>
              </div>
            ) : (
              <div style={{ maxHeight: "400px", overflowY: "auto" }} className="tw-space-y-2">
                {form.ficha_tecnica[0].map((f, index) => (
                  <div key={index} className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200 hover:tw-border-teal-200 tw-transition-all">
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <div className="tw-w-8 tw-h-8 tw-rounded-full bg-dark tw-text-white tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-semibold">
                        {index + 1}
                      </div>
                      <div>
                        <div className="tw-font-semibold tw-text-gray-800 tw-text-sm">{f.nombre}</div>
                        <div className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
                          <i className="bi bi-briefcase text-dark"></i>
                          {f.rol}
                        </div>
                      </div>
                    </div>
                    <button
                      className="btn btn-outline-danger btn-sm ms-2 tw-transition-all tw-duration-200 hover:tw-scale-110 hover:tw-bg-red-500 hover:tw-text-white"
                      onClick={(event) => {
                        event.target.name = "ficha_tecnica-delete";
                        onClick(event, index);
                      }}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className=" p-3 mb-4 tw-bg-white " style={{ borderRadius: "1rem" }}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <h5 className="d-flex align-items-center tw-text-2xl tw-font-bold tw-text-gray-800 tw-mb-3">
              <i className="bi bi-link-45deg me-2 text-dark tw-text-blue-600 tw-text-3xl tw-mr-3"></i>
              Links para mi evento
              <span className="badge bg-secondary ms-2 tw-ml-3 tw-px-3 tw-py-1 tw-text-xs tw-bg-gray-400 tw-text-white tw-rounded-full" style={{ fontSize: "0.7rem" }}>Opcional</span>
            </h5>
            <p className="text-muted small tw-flex tw-items-center tw-text-gray-600 tw-bg-blue-50 tw-p-3 tw-rounded-lg tw-mb-4">
              <i className="bi bi-info-circle me-1 tw-text-blue-500 tw-text-lg tw-mr-2"></i>
              Agrega enlaces a redes sociales, videos, sitios web relacionados al evento
            </p>
            <hr className="tw-border-t-2 tw-border-gray-200 tw-my-4" />
            <Input
              onChange={onChange}
              label={"Nombre del enlace"}
              name={"link-nombre"}
              type={"text"}
              value={form["link-nombre"][0]}
              icon={'<i class="bi bi-tag"></i>'}
              validate={validation["link-nombre"]}
              disabled={false}
            />
            <Input
              onChange={onChange}
              label={"URL completa (https://...)"}
              name={"link-url"}
              type={"url"}
              value={form["link-url"][0]}
              icon={'<i class="bi bi-globe"></i>'}
              validate={validation["link-url"]}
              disabled={false}
            />
            <label className="btn btn-primary fw-normal GothamBold text-white w-100 mt-1   tw-shadow-lg hover:tw-shadow-xl tw-rounded-xl tw-py-3" style={{ cursor: "pointer" }}>
              <i className="bi bi-plus-circle me-2 tw-text-lg"></i>
              Añadir link
              <button
                className="d-none"
                name="link"
                onClick={(event) => onClick(event)}
              ></button>
            </label>
          </div>

          <div className="col-md-6">
            <h6 className="text-muted mb-3 tw-flex tw-items-center tw-text-gray-700 tw-font-bold tw-text-lg tw-mb-4">
              <i className="bi bi-list-ul me-2 tw-text-blue-600 tw-text-xl tw-mr-2"></i>
              Enlaces agregados ({form.link[0].length})
            </h6>
            {form.link[0].length === 0 ? (
              <div className="text-center text-muted py-5 border rounded bg-light tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-gray-50 tw-border-2 tw-border-dashed tw-border-gray-300 tw-rounded-2xl tw-py-12">
                <i className="bi bi-link tw-text-7xl tw-text-gray-300 tw-mb-4"></i>
                <p className="mt-2 tw-text-gray-500 tw-font-medium">Aún no hay enlaces agregados</p>
              </div>
            ) : (
              <div style={{ maxHeight: "400px", overflowY: "auto" }} className="tw-space-y-3">
                <ul class="list-group tw-space-y-2">
                  {form.link[0].map((l, index) => (
                    <li key={index} class="list-group-item border text-muted d-flex justify-content-between align-items-center shadow-sm mb-2 tw-bg-white tw-border-2 tw-border-gray-200 tw-rounded-xl tw-p-4 tw-transition-all hover:tw-shadow-lg hover:tw-scale-[1.01]">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-1 tw-flex tw-items-center tw-mb-2">
                          <i className="bi bi-link-45deg text-dark tw-text-blue-500 tw-text-2xl tw-mr-2"></i>
                          <span className="text-black fw-bold tw-text-gray-800 tw-font-bold">{l.nombre}</span>
                        </div>
                        <a
                          href={l.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="small text-dark text-decoration-none d-block text-truncate tw-flex tw-items-center tw-text-blue-600 hover:tw-text-blue-800 tw-transition-colors"
                          style={{ maxWidth: "300px" }}
                        >
                          <i className="bi bi-box-arrow-up-right me-1 tw-mr-1"></i>
                          {l.url}
                        </a>
                      </div>
                      <label
                        className="btn btn-outline-danger btn-sm ms-2 tw-transition-all tw-duration-200 hover:tw-scale-110 hover:tw-bg-red-500 hover:tw-text-white"
                        style={{ cursor: "pointer" }}
                      >
                        <i class="bi bi-trash"></i>
                        <button
                          className="d-none"
                          name="link-delete"
                          onClick={(event) => onClick(event, index)}
                        ></button>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border p-3 mb-4 tw-bg-white tw-shadow-lg tw-border-gray-200 tw-transition-all hover:tw-shadow-xl" style={{ borderRadius: "1rem" }}>
        <div className="row">
          <div className="col-md-12 mb-3">
            <h5 className="d-flex align-items-center tw-text-2xl tw-font-bold tw-text-gray-800 tw-mb-3">
              <i className="bi bi-images me-2 text-dark tw-text-pink-600 tw-text-3xl tw-mr-3"></i>
              Material de Difusión
              <span className="badge bg-secondary ms-2 tw-ml-3 tw-px-3 tw-py-1 tw-text-xs tw-bg-gray-400 tw-text-white tw-rounded-full" style={{ fontSize: "0.7rem" }}>Opcional</span>
            </h5>
            <p className="text-muted small tw-flex tw-items-center tw-text-gray-600 tw-bg-pink-50 tw-p-3 tw-rounded-lg tw-mb-4">
              <i className="bi bi-info-circle me-1 tw-text-pink-500 tw-text-lg tw-mr-2"></i>
              Puedes subir hasta 2 imágenes o fotografías del espectáculo sin logo ni texto
            </p>
            <hr className="tw-border-t-2 tw-border-gray-200 tw-my-4" />

            {loadingData && (
              <div className="alert alert-info d-flex align-items-center tw-bg-blue-50 tw-border-l-4 tw-border-blue-500 tw-p-4 tw-rounded-r-lg tw-mb-4">
                <div className="spinner-border spinner-border-sm me-2 tw-text-blue-600" role="status"></div>
                <span className="tw-text-blue-700 tw-font-medium">Cargando material de difusión...</span>
              </div>
            )}

            {/* Zona de drag and drop */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border rounded p-5 text-center tw-rounded-2xl tw-transition-all tw-duration-300 tw-transform ${isDragging
                  ? "border-primary bg-light tw-border-pink-500 tw-bg-pink-50 tw-scale-[1.02] tw-shadow-2xl"
                  : "border-secondary tw-border-gray-300 hover:tw-border-pink-400 hover:tw-bg-gray-50"
                }`}
              style={{
                borderStyle: "dashed",
                borderWidth: "3px",
                transition: "all 0.3s ease",
                cursor: "pointer",
              }}
            >
              <input
                type="file"
                id="material-difusion-input"
                multiple
                accept="image/*"
                onChange={handleFileInputChange}
                style={{ display: "none" }}
                disabled={
                  (form.id && form.id[0] ? materialDifusion.length : materialPendiente.length) >= 2
                }
              />
              <label
                htmlFor="material-difusion-input"
                style={{ cursor: "pointer", width: "100%" }}
                className="tw-cursor-pointer"
              >
                <div>
                  {isDragging ? (
                    <div className="tw-animate-bounce">
                      <i className="bi bi-cloud-upload-fill tw-text-7xl tw-text-pink-600 tw-mb-4"></i>
                      <p className="mt-3 mb-0 text-muted tw-text-xl tw-text-pink-700 tw-font-bold">
                        ¡Suelta las imágenes aquí!
                      </p>
                    </div>
                  ) : (
                    <div className="tw-transition-all tw-duration-300 hover:tw-scale-105">
                      <i className="bi bi-cloud-upload tw-text-7xl tw-text-gray-400 tw-mb-3 tw-block"></i>
                      <p className="mt-3 mb-0 text-muted tw-text-lg tw-text-gray-700 tw-font-semibold">
                        {uploadingMaterial
                          ? "Subiendo imágenes..."
                          : "Arrastra imágenes aquí o haz clic para seleccionar"}
                      </p>
                      <small className="text-muted tw-px-4 tw-py-2 tw-bg-gray-100 tw-rounded-full tw-text-xs tw-text-gray-600 tw-mt-3 tw-inline-block">
                        Formatos: JPG, PNG, GIF. Puedes subir múltiples archivos.
                      </small>
                    </div>
                  )}
                </div>
              </label>
            </div>

            {/* Contador de archivos */}
            <div className="mt-3 d-flex justify-content-between align-items-center tw-mt-4 tw-flex tw-justify-between tw-items-center">
              <small className={`badge tw-px-4 tw-py-2 tw-rounded-full tw-font-semibold tw-transition-all ${
                (form.id && form.id[0] ? materialDifusion.length : materialPendiente.length) === 0
                  ? 'bg-secondary tw-bg-gray-400'
                  : (form.id && form.id[0] ? materialDifusion.length : materialPendiente.length) >= 2
                    ? 'bg-success tw-bg-green-500'
                    : 'bg-info text-dark tw-bg-blue-400 tw-text-white'
              }`}>
                <i className="bi bi-image me-1"></i>
                {form.id && form.id[0] ? materialDifusion.length : materialPendiente.length} / 2 imágenes cargadas
              </small>
              {uploadingMaterial && (
                <small className="badge bg-warning text-dark tw-bg-yellow-400 tw-text-gray-800 tw-px-4 tw-py-2 tw-rounded-full">
                  <span className="spinner-border spinner-border-sm me-1 tw-mr-2"></span>
                  Subiendo...
                </small>
              )}
            </div>

            {/* Vista previa de archivos pendientes (para eventos nuevos) */}
            {!form.id[0] && materialPendiente.length > 0 && (
              <div className="mt-4">
                <h6>Imágenes pendientes ({materialPendiente.length}/2):</h6>
                <div className="row">
                  {materialPendiente.map((file, index) => (
                    <div key={index} className="col-md-3 col-sm-6 mb-3">
                      <div
                        className="border shadow-sm"
                        style={{ position: "relative" }}
                      >
                        <img
                          src={URL.createObjectURL(file)}
                          alt="Material pendiente"
                          className="card-img-top"
                          style={{
                            height: "200px",
                            objectFit: "cover",
                          }}
                        />
                        <button
                          onClick={() => {
                            const url = URL.createObjectURL(file);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = file.name;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                          }}
                          className="btn btn-primary btn-sm"
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "50px",
                          }}
                        >
                          <i className="bi bi-download"></i>
                        </button>
                        <button
                          onClick={() =>
                            setMaterialPendiente((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="btn btn-danger btn-sm"
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "5px",
                          }}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                        <div
                          className="badge bg-warning text-dark"
                          style={{
                            position: "absolute",
                            bottom: "5px",
                            left: "5px",
                          }}
                        >
                          Pendiente
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Galería de imágenes subidas */}
            {form.id[0] && materialDifusion.length > 0 && (
              <div className="mt-4">
                <h6>Imágenes cargadas ({materialDifusion.length}/2):</h6>
                <div className="row">
                  {materialDifusion.map((material) => (
                    <div
                      key={material.id}
                      className="col-md-3 col-sm-6 mb-3"
                    >
                      <div
                        className="border shadow-sm"
                        style={{ position: "relative" }}
                      >
                        <img
                          src={material.url}
                          alt="Material de difusión"
                          className="card-img-top"
                          style={{
                            height: "200px",
                            objectFit: "cover",
                          }}
                        />
                        <button
                          onClick={() => descargarImagen(material.url, `material-difusion-${material.id}.jpg`)}
                          className="btn btn-primary btn-sm"
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "50px",
                          }}
                        >
                          <i className="bi bi-download"></i>
                        </button>
                        <button
                          onClick={() => eliminarMaterialDifusion(material.id)}
                          className="btn btn-danger btn-sm"
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "5px",
                          }}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default Que;
